import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay, Image, Text } from '@chakra-ui/react';
import { ShoppingBagOutlined } from '@mui/icons-material';

import { useCartContext } from '../../contexts/CartContext';
import { useShopUI } from '../../contexts/ShopUIContext';
import SizeSelector from './SizeSelector';
import useIsPhone from '../../hooks/useIsPhone';
import AddedLabel from './AddedLabel';
import { formatPrice, swatchColor, productPath } from './productUtils';

// Quick add from a product card (design-system.md §4.6): right drawer on desktop, bottom sheet on phones
const QuickAddDrawer = () => {

    const navigate = useNavigate();
    const { quickAddProduct: product, closeQuickAdd, openCart } = useShopUI();
    const { addItem } = useCartContext();
    const placement = useIsPhone() ? 'bottom' : 'right';
    const [size, setSize] = useState('');
    const [added, setAdded] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => { setSize(product?.sizes?.length === 1 ? product.sizes[0] : ''); setAdded(false); setError(false); }, [product]);

    const add = () => {
        if (!size) { setError(true); return; }
        addItem(product, size, 1);
        setAdded(true);
        setTimeout(() => { closeQuickAdd(); openCart(); }, 900);
    };

    return (
        <Drawer isOpen={!!product} placement={placement} onClose={closeQuickAdd} size='sm' >
            <DrawerOverlay />
            <DrawerContent borderTopRadius={{ base: '20px', md: 0 }} maxHeight={{ base: '88vh', md: '100vh' }} >
                <DrawerCloseButton top='14px' />
                <DrawerHeader fontSize='16px' fontWeight={600} borderBottom='1px solid' borderColor='raano.border' >Quick add</DrawerHeader>
                {product &&
                    <DrawerBody px={{ base: '16px', md: '20px' }} py='20px' >
                        <Box display='flex' gap='14px' >
                            <Box width={{ base: '110px', md: '140px' }} flexShrink={0} bg='raano.imageBg' sx={{ aspectRatio: '2 / 3' }} >
                                <Image src={product.imageUrl} alt={product.name} width='100%' height='100%' objectFit='cover' />
                            </Box>
                            <Box>
                                <Text fontSize='16px' fontWeight={500} lineHeight='1.3' >{product.name}</Text>
                                <Text textStyle='priceLarge' mt='6px' >{formatPrice(product.price)}</Text>
                                {product.color &&
                                    <Box display='flex' alignItems='center' gap='8px' mt='12px' fontSize='14px' >
                                        <Box width='17px' height='17px' borderRadius='6px' border='1px solid' borderColor='raano.inkSoft' p='1px' ><Box width='100%' height='100%' borderRadius='5px' bg={swatchColor(product.color)} /></Box>
                                        <Text textTransform='capitalize' >{product.color}</Text>
                                    </Box>}
                            </Box>
                        </Box>
                        <Box mt='24px' >
                            <SizeSelector sizes={product.sizes} value={size} onChange={(s) => { setSize(s); setError(false); }} compact />
                            {error && <Text mt='8px' fontSize='13px' color='raano.error' >Please select a size.</Text>}
                        </Box>
                    </DrawerBody>}
                {product &&
                    <DrawerFooter gap='10px' borderTop='1px solid' borderColor='raano.border' pb={{ base: 'calc(16px + env(safe-area-inset-bottom))', md: '16px' }} >
                        <Button variant='wipe' flex={1} height='48px' fontSize='16px' onClick={() => { closeQuickAdd(); navigate(productPath(product), { state: { productId: product._id } }); }} >View product</Button>
                        <Button variant='ink' flex={1} height='48px' fontSize='16px' onClick={add} >
                            <AddedLabel added={added} ><ShoppingBagOutlined sx={{ fontSize: 18 }} />Add to cart</AddedLabel>
                        </Button>
                    </DrawerFooter>}
            </DrawerContent>
        </Drawer>
    );
};

export default QuickAddDrawer;
